import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import { MIGRATION_ENABLED, MIGRATION_POLLING } from '@/config/migration';
import {
  isMigrationApiError,
  migrationApi,
  MigrationApiError,
} from '@/lib/api-clients/rest-client';
import { useAccountStore } from '@/store/use-account-store';
import { EventType } from '@/types';
import {
  MIGRATION_STATES,
  MigrationOptInResponse,
  MigrationState,
  MigrationStatus,
  MigrationStatusResponse,
} from '@/types/migration';
import { sendEvent } from '@/utils/analytics';

const STATUS_PATH = '/v3/migration/status';
const OPT_IN_PATH = '/v3/migration/opt-in';

export const getMigrationStatusQueryKey = (address: string | undefined) =>
  ['migration', 'status', address?.toLowerCase() ?? null] as const;

export const normalizeMigrationState = (state: string): MigrationState =>
  (MIGRATION_STATES as readonly string[]).includes(state)
    ? (state as MigrationState)
    : 'unknown';

export async function fetchMigrationStatus(): Promise<MigrationStatus> {
  try {
    const res = await migrationApi.get<MigrationStatusResponse>(STATUS_PATH);
    return {
      started: true,
      bundleId: res.bundle_id,
      state: normalizeMigrationState(res.state),
      accounts: (res.accounts ?? []).map((account) =>
        account.address.toLowerCase(),
      ),
    };
  } catch (error) {
    if (isMigrationApiError(error) && error.status === 404) {
      return { started: false };
    }
    throw error;
  }
}

export const optInMigration = () =>
  migrationApi.post<MigrationOptInResponse>(OPT_IN_PATH);

export function getMigrationRefetchInterval(
  status: MigrationStatus | undefined,
  error: MigrationApiError | null,
): number | false {
  if (error) {
    if (error.status === 401) return false;
    return MIGRATION_POLLING.backoff;
  }
  if (!status?.started) return false;
  switch (status.state) {
    case 'pending':
    case 'in_progress':
    case 'unknown':
      return MIGRATION_POLLING.active;
    case 'blocked':
    case 'failed':
      return MIGRATION_POLLING.parked;
    case 'completed':
      return false;
  }
}

export const getMigrationStatusQueryOptions = (address: string | undefined) =>
  queryOptions<MigrationStatus, MigrationApiError>({
    queryKey: getMigrationStatusQueryKey(address),
    queryFn: fetchMigrationStatus,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: true,
    refetchInterval: (query) =>
      getMigrationRefetchInterval(query.state.data, query.state.error),
  });

export function useMigrationStatus() {
  const { address, hasAuth } = useAccountStore(
    useShallow((state) => ({
      address: state.address,
      hasAuth: state.hasAuth,
    })),
  );

  return useQuery({
    ...getMigrationStatusQueryOptions(address),
    enabled: MIGRATION_ENABLED && hasAuth && !!address,
  });
}

export type OptInFailure =
  /** `409 1130` — already opted in (or migrated): show the status instead. */
  | 'already_exists'
  /** `403 1005` / `400 1130` / `404 1099` — the account cannot be migrated, an operator looks at it. No retry loop. */
  | 'not_eligible'
  /** `401 1002` — sign-in flow. */
  | 'unauthorized'
  /** `429` — back off, retry once after a few seconds. */
  | 'rate_limited'
  /** `500` / `503 1004` — allow a manual retry. */
  | 'server_error';

export function getOptInFailure(error: MigrationApiError): OptInFailure {
  switch (error.status) {
    case 409:
      return 'already_exists';
    case 401:
      return 'unauthorized';
    case 429:
      return 'rate_limited';
    case 400:
    case 403:
    case 404:
      return 'not_eligible';
    default:
      return 'server_error';
  }
}

export function useOptInMigration() {
  const queryClient = useQueryClient();
  const address = useAccountStore((state) => state.address);
  const queryKey = getMigrationStatusQueryKey(address);

  return useMutation<MigrationOptInResponse, MigrationApiError, void>({
    mutationFn: optInMigration,
    onSuccess: (res) => {
      sendEvent(EventType.MIGRATION_OPT_IN, {
        bundleId: res.bundle_id,
        state: res.state,
      });
      queryClient.setQueryData<MigrationStatus>(queryKey, {
        started: true,
        bundleId: res.bundle_id,
        state: normalizeMigrationState(res.state),
        accounts: [],
      });
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (error) => {
      if (error.status === 409) {
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}
